import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@artstorefronts/ui';
import { Loader2, Plus, Trash2, Zap } from 'lucide-react';
import { toast } from 'sonner';

import AdminLayout from '../../../components/AdminLayout';

const ADMIN_SPARKLINKS = gql`
  query AdminSparklinks {
    adminSparklinks {
      sparklinks {
        id
        sparkId
        message
        text
        buttonText
        isHidden
        lastShownOn
        isLiveToday
      }
    }
  }
`;

const ADMIN_SAVE_SPARKLINKS = gql`
  mutation AdminSaveSparklinks($input: AdminSaveSparklinksInput!) {
    adminSaveSparklinks(input: $input) {
      sparklinks {
        id
        sparkId
        message
        text
        buttonText
        isHidden
        lastShownOn
        isLiveToday
      }
    }
  }
`;

const ADMIN_FORCE_LIVE_SPARKLINK = gql`
  mutation AdminForceLiveSparklink($id: ID!, $resetEngagement: Boolean!) {
    adminForceLiveSparklink(id: $id, resetEngagement: $resetEngagement) {
      sparklinks {
        id
        sparkId
        message
        text
        buttonText
        isHidden
        lastShownOn
        isLiveToday
      }
    }
  }
`;

const ADMIN_DELETE_SPARKLINK = gql`
  mutation AdminDeleteSparklink($id: ID!) {
    adminDeleteSparklink(id: $id)
  }
`;

interface ServerSparklink {
  id: string;
  sparkId: string;
  message: string;
  text: string;
  buttonText: string;
  isHidden: boolean;
  lastShownOn: string | null;
  isLiveToday: boolean;
}

interface EditorSparklink extends Omit<ServerSparklink, 'id'> {
  clientId: string;
  id?: string;
}

function createEmptySparklink(): EditorSparklink {
  return {
    clientId: `new-${crypto.randomUUID()}`,
    sparkId: '',
    message: '',
    text: '',
    buttonText: '',
    isHidden: false,
    lastShownOn: null,
    isLiveToday: false,
  };
}

function mapServerSparklink(sparklink: ServerSparklink): EditorSparklink {
  return {
    ...sparklink,
    clientId: sparklink.id,
  };
}

function rowsEqual(a: EditorSparklink, b: EditorSparklink): boolean {
  return (
    a.clientId === b.clientId &&
    a.id === b.id &&
    a.sparkId === b.sparkId &&
    a.message === b.message &&
    a.text === b.text &&
    a.buttonText === b.buttonText &&
    a.isHidden === b.isHidden
  );
}

function sparklinksEqual(a: EditorSparklink[], b: EditorSparklink[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((sparklink, index) => rowsEqual(sparklink, b[index]));
}

// "Set live" acts on persisted state, so the button itself gates on the saved
// baseline, not the working row — an unsaved edit must never enable or disable
// it on state the server will not see. The caption order matters too: unsaved
// changes are checked before the hidden state so the message always matches
// what the admin sees on screen, not a stale saved value.
function liveHint(row: EditorSparklink, saved: EditorSparklink | undefined): string | null {
  if (!saved?.id) {
    return 'Save this row before setting it live.';
  }

  if (!rowsEqual(row, saved)) {
    return 'Save this row before setting it live.';
  }

  if (saved.isHidden) {
    return "Hidden sparklinks can't be set live.";
  }

  return null;
}

function patchLiveState(rows: EditorSparklink[], serverSparklinks: ServerSparklink[]): EditorSparklink[] {
  const byId = new Map(serverSparklinks.map((sparklink) => [sparklink.id, sparklink]));

  return rows.map((row) => {
    if (!row.id) {
      return row;
    }

    const match = byId.get(row.id);
    if (!match) {
      return row;
    }

    return { ...row, isLiveToday: match.isLiveToday, lastShownOn: match.lastShownOn };
  });
}

function liveToast(resetEngagement: boolean): string {
  if (resetEngagement) {
    return "Sparklink is now live. Today's engagement was reset.";
  }

  return 'Sparklink is now live.';
}

// Single source of truth for the table's columns: the header renders from it and
// the full-width hint row spans `COLUMNS.length`, so adding a column can't leave
// a stale colSpan behind.
const COLUMNS = [
  { label: 'Spark ID', className: 'w-[8%]' },
  { label: 'Message', className: 'w-[21%]' },
  { label: 'Bubble text', className: 'w-[23%]' },
  { label: 'Button text', className: 'w-[17%]' },
  { label: 'Hidden', className: 'w-[7%] text-center' },
  { label: 'Last live', className: 'w-[12%]' },
  { label: 'Actions', className: 'w-[12%] text-right', isLabelHidden: true },
];

function SparklinkRow({
  sparklink,
  hint,
  index,
  isDeleting,
  isForcingLive,
  onUpdate,
  onDelete,
  onForceLive,
}: {
  sparklink: EditorSparklink;
  hint: string | null;
  index: number;
  isDeleting: boolean;
  isForcingLive: boolean;
  onUpdate: (clientId: string, updates: Partial<EditorSparklink>) => void;
  onDelete: (clientId: string) => Promise<boolean>;
  onForceLive: (clientId: string, resetEngagement: boolean) => Promise<boolean>;
}) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmSetLive, setConfirmSetLive] = useState(false);
  const [resetEngagement, setResetEngagement] = useState(false);
  const rowNumber = index + 1;

  async function handleConfirmDelete() {
    if (await onDelete(sparklink.clientId)) {
      setConfirmDelete(false);
    }
  }

  function handleOpenSetLive() {
    setResetEngagement(false);
    setConfirmSetLive(true);
  }

  // `AlertDialogAction` is a Radix `Close`, which dismisses the dialog on click
  // unless the handler prevents the default. Holding it open keeps the spinner
  // visible, blocks a second click, and preserves the checkbox on failure.
  async function handleConfirmSetLive(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (await onForceLive(sparklink.clientId, resetEngagement)) {
      setConfirmSetLive(false);
    }
  }

  // The caption exists only to explain why "Set live" is unavailable, so it
  // follows that button: a row already live today has no button to explain.
  const setLiveHint = sparklink.isLiveToday ? null : hint;

  // A hint gets its own full-width row underneath, so drop this row's bottom
  // border to keep the pair reading as one sparklink.
  const rowClassName = [sparklink.isHidden && 'opacity-60', setLiveHint && 'border-b-0'].filter(Boolean).join(' ');

  return (
    <>
      <TableRow className={rowClassName}>
        <TableCell className="align-top">
          <Label htmlFor={`spark-id-${sparklink.clientId}`} className="sr-only">
            Spark ID for sparklink {rowNumber}
          </Label>
          <Input
            id={`spark-id-${sparklink.clientId}`}
            type="number"
            min={1}
            value={sparklink.sparkId}
            onChange={(event) => onUpdate(sparklink.clientId, { sparkId: event.target.value })}
          />
        </TableCell>
        <TableCell className="align-top">
          <Label htmlFor={`message-${sparklink.clientId}`} className="sr-only">
            Message for sparklink {rowNumber}
          </Label>
          <Input
            id={`message-${sparklink.clientId}`}
            value={sparklink.message}
            onChange={(event) => onUpdate(sparklink.clientId, { message: event.target.value })}
          />
        </TableCell>
        <TableCell className="align-top">
          <Label htmlFor={`text-${sparklink.clientId}`} className="sr-only">
            Bubble text for sparklink {rowNumber}
          </Label>
          <Textarea
            id={`text-${sparklink.clientId}`}
            rows={2}
            value={sparklink.text}
            onChange={(event) => onUpdate(sparklink.clientId, { text: event.target.value })}
          />
        </TableCell>
        <TableCell className="align-top">
          <Label htmlFor={`button-text-${sparklink.clientId}`} className="sr-only">
            Button text for sparklink {rowNumber}. Arrow icon is added automatically.
          </Label>
          <Input
            id={`button-text-${sparklink.clientId}`}
            value={sparklink.buttonText}
            onChange={(event) => onUpdate(sparklink.clientId, { buttonText: event.target.value })}
            aria-describedby={`button-text-hint-${sparklink.clientId}`}
          />
          <p id={`button-text-hint-${sparklink.clientId}`} className="mt-1 text-xs text-gray-500">
            Arrow icon is added automatically; do not include a chevron.
          </p>
        </TableCell>
        <TableCell className="align-top text-center">
          <Label htmlFor={`hidden-${sparklink.clientId}`} className="sr-only">
            Hidden for sparklink {rowNumber}
          </Label>
          <Checkbox
            id={`hidden-${sparklink.clientId}`}
            checked={sparklink.isHidden}
            onCheckedChange={(checked) => onUpdate(sparklink.clientId, { isHidden: checked === true })}
          />
        </TableCell>
        <TableCell className="align-top whitespace-nowrap text-sm text-gray-500">
          <div>{sparklink.lastShownOn ?? '—'}</div>
          {sparklink.isLiveToday && <div className="mt-1 text-xs font-medium text-gray-700">Live today</div>}
        </TableCell>
        <TableCell className="align-top text-right">
          <div className="flex flex-col items-end gap-1">
            {!sparklink.isLiveToday && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleOpenSetLive}
                disabled={hint !== null}
                aria-label={`Set sparklink ${rowNumber} live`}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <Zap className="h-4 w-4" />
                Set live
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setConfirmDelete(true)}
              aria-label={`Delete sparklink ${rowNumber}`}
              className="text-gray-400 hover:text-red-600"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>

          <AlertDialog open={confirmSetLive} onOpenChange={setConfirmSetLive}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Set this sparklink live?</AlertDialogTitle>
                <AlertDialogDescription>
                  This replaces today&apos;s sparklink for every member right away, until tomorrow&apos;s rollover.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="space-y-1 text-left">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id={`reset-engagement-${sparklink.clientId}`}
                    checked={resetEngagement}
                    onCheckedChange={(checked) => setResetEngagement(checked === true)}
                  />
                  <Label htmlFor={`reset-engagement-${sparklink.clientId}`}>
                    Also let members who already saw today&apos;s Sparklink see this one
                  </Label>
                </div>
                <p className="text-xs text-gray-500">
                  Only affects people who already dismissed or clicked through today; not a permanent setting.
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isForcingLive}>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleConfirmSetLive} disabled={isForcingLive}>
                  {isForcingLive ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set live'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete sparklink?</AlertDialogTitle>
                <AlertDialogDescription>
                  This removes this sparklink from the daily pool immediately.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete sparklink'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TableCell>
      </TableRow>

      {setLiveHint && (
        <TableRow className={sparklink.isHidden ? 'opacity-60' : undefined}>
          <TableCell colSpan={COLUMNS.length} className="pt-0 pb-3 text-right text-xs text-gray-500">
            {setLiveHint}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function AdminSparklinksPage() {
  const [sparklinks, setSparklinks] = useState<EditorSparklink[]>([]);
  const [savedSparklinks, setSavedSparklinks] = useState<EditorSparklink[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isForcingLive, setIsForcingLive] = useState(false);
  const isDirtyRef = useRef(false);
  const { data, loading } = useQuery(ADMIN_SPARKLINKS, { fetchPolicy: 'cache-and-network' });
  const [saveSparklinks, { loading: saving }] = useMutation(ADMIN_SAVE_SPARKLINKS);
  const [deleteSparklink] = useMutation(ADMIN_DELETE_SPARKLINK);
  const [forceLiveSparklink] = useMutation(ADMIN_FORCE_LIVE_SPARKLINK);

  useEffect(() => {
    if (!data?.adminSparklinks || isDirtyRef.current) {
      return;
    }

    const mapped = data.adminSparklinks.sparklinks.map(mapServerSparklink);
    setSparklinks(mapped);
    setSavedSparklinks(mapped);
  }, [data]);

  const hasUnsavedChanges = useMemo(
    () => sparklinksEqual(sparklinks, savedSparklinks) === false,
    [sparklinks, savedSparklinks]
  );

  const savedByClientId = useMemo(
    () => new Map(savedSparklinks.map((sparklink) => [sparklink.clientId, sparklink])),
    [savedSparklinks]
  );

  useEffect(() => {
    isDirtyRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  const handleUpdateSparklink = (clientId: string, updates: Partial<EditorSparklink>) => {
    setSparklinks((previous) =>
      previous.map((sparklink) => (sparklink.clientId === clientId ? { ...sparklink, ...updates } : sparklink))
    );
  };

  const handleAddSparklink = () => {
    setSparklinks((previous) => [...previous, createEmptySparklink()]);
  };

  const handleDeleteSparklink = async (clientId: string): Promise<boolean> => {
    const sparklink = sparklinks.find((item) => item.clientId === clientId);
    if (!sparklink) {
      return false;
    }

    if (!sparklink.id) {
      setSparklinks((previous) => previous.filter((item) => item.clientId !== clientId));
      return true;
    }

    setIsDeleting(true);
    try {
      await deleteSparklink({ variables: { id: sparklink.id } });
      setSparklinks((previous) => previous.filter((item) => item.clientId !== clientId));
      setSavedSparklinks((previous) => previous.filter((item) => item.clientId !== clientId));
      toast.success('Sparklink deleted.');
      return true;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete sparklink.');
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleForceLive = async (clientId: string, resetEngagement: boolean): Promise<boolean> => {
    const sparklink = sparklinks.find((item) => item.clientId === clientId);
    if (!sparklink?.id) {
      return false;
    }

    setIsForcingLive(true);
    try {
      const result = await forceLiveSparklink({ variables: { id: sparklink.id, resetEngagement } });
      const forced = result.data?.adminForceLiveSparklink;
      if (!forced) {
        throw new Error('Set live failed');
      }

      setSparklinks((previous) => patchLiveState(previous, forced.sparklinks));
      setSavedSparklinks((previous) => patchLiveState(previous, forced.sparklinks));
      toast.success(liveToast(resetEngagement));
      return true;
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to set sparklink live.');
      return false;
    } finally {
      setIsForcingLive(false);
    }
  };

  const handleSave = async () => {
    try {
      const result = await saveSparklinks({
        variables: {
          input: {
            sparklinks: sparklinks.map((sparklink) => ({
              ...(sparklink.id ? { id: sparklink.id } : {}),
              sparkId: sparklink.sparkId,
              message: sparklink.message,
              text: sparklink.text,
              buttonText: sparklink.buttonText,
              isHidden: sparklink.isHidden,
            })),
          },
        },
      });
      const saved = result.data?.adminSaveSparklinks;
      if (!saved) {
        throw new Error('Save failed');
      }

      const mapped = saved.sparklinks.map(mapServerSparklink);
      setSparklinks(mapped);
      setSavedSparklinks(mapped);
      toast.success('Sparklinks saved.');
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to save sparklinks.');
    }
  };

  if (loading && !data) {
    return (
      <AdminLayout
        title="Sparklinks"
        description="One sparklink goes live per day, the same one for every member. Newly added sparklinks are shown before anything from the last 60 days."
      >
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Sparklinks"
      description="One sparklink goes live per day, the same one for every member. Newly added sparklinks are shown before anything from the last 60 days."
      action={
        <Button onClick={handleAddSparklink} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add sparklink
        </Button>
      }
    >
      <div className="space-y-6">
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <Table className="min-w-[1100px] table-fixed">
            <TableHeader>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableHead key={column.label} className={column.className}>
                    <span className={column.isLabelHidden ? 'sr-only' : undefined}>{column.label}</span>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sparklinks.map((sparklink, index) => (
                <SparklinkRow
                  key={sparklink.clientId}
                  sparklink={sparklink}
                  hint={liveHint(sparklink, savedByClientId.get(sparklink.clientId))}
                  index={index}
                  isDeleting={isDeleting}
                  isForcingLive={isForcingLive}
                  onUpdate={handleUpdateSparklink}
                  onDelete={handleDeleteSparklink}
                  onForceLive={handleForceLive}
                />
              ))}
            </TableBody>
          </Table>
          {sparklinks.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">
              No sparklinks yet. Add a sparklink to get started.
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-4">
          <Button variant="ghost" onClick={handleAddSparklink} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add sparklink
          </Button>
          <Button onClick={handleSave} disabled={saving || isDeleting || isForcingLive || !hasUnsavedChanges}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminSparklinksPage;
